// Recording-gap detection for driver GPS trails.
//
// The driver app records a fix every ~2s and nothing in the pipeline filters
// or interpolates, so whenever the app stops recording (GPS loss, app killed,
// OS doze) the trail has two consecutive points far apart in time/space and a
// naive polyline draws a straight line cutting across streets. We split the
// trail at those gaps: solid segments for recorded driving, and gap
// connectors the map renders as dotted lines meaning "no data here".

export interface LatLng {
    lat: number;
    lng: number;
}

export interface SplitTrail {
    // Runs of consecutively-recorded points (each has >= 2 points)
    segments: LatLng[][];
    // Straight connectors between adjacent segments, [gapStart, gapEnd]
    gaps: [LatLng, LatLng][];
}

// A segment is a recording gap when the device went silent for a while or the
// implied jump is farther than a car plausibly drives between two fixes.
export const GAP_MIN_MS = 60_000;
export const GAP_MIN_METERS = 500;

const metersBetween = (a: LatLng, b: LatLng) => {
    const dy = (b.lat - a.lat) * 111_320;
    const dx = (b.lng - a.lng) * 111_320 * Math.cos(((a.lat + b.lat) / 2) * (Math.PI / 180));
    return Math.sqrt(dx * dx + dy * dy);
};

// gapBefore[i] === true means the segment between point i-1 and i is a gap.
// Mirrors TrailReplay's timestamp handling: epoch seconds vs millis is decided
// from the first point, and unusable timestamps fall back to distance only.
export const detectGapsBefore = (trackPoints: any[]): boolean[] => {
    const pts = trackPoints || [];
    const out = new Array(pts.length).fill(false);
    const firstTs = Number(pts[0]?.timestamp) || 0;
    const scale = firstTs > 1e11 ? 1 : 1000; // epoch millis vs epoch seconds
    for (let i = 1; i < pts.length; i++) {
        const a = pts[i - 1];
        const b = pts[i];
        if (
            !Number.isFinite(a?.latitude) ||
            !Number.isFinite(a?.longitude) ||
            !Number.isFinite(b?.latitude) ||
            !Number.isFinite(b?.longitude)
        ) {
            continue;
        }
        const tsA = Number(a?.timestamp) || 0;
        const tsB = Number(b?.timestamp) || 0;
        const dtMs = tsA > 0 && tsB > 0 ? (tsB - tsA) * scale : 0;
        const dist = metersBetween(
            { lat: a.latitude, lng: a.longitude },
            { lat: b.latitude, lng: b.longitude }
        );
        out[i] = dtMs >= GAP_MIN_MS || dist >= GAP_MIN_METERS;
    }
    return out;
};

// Split a raw trackPoints array into solid segments + dotted gap connectors.
export const splitTrailAtGaps = (trackPoints: any[]): SplitTrail => {
    const pts = (trackPoints || []).filter(
        (tp: any) => Number.isFinite(tp?.latitude) && Number.isFinite(tp?.longitude)
    );
    const gapBefore = detectGapsBefore(pts);
    const segments: LatLng[][] = [];
    const gaps: [LatLng, LatLng][] = [];
    let current: LatLng[] = [];
    for (let i = 0; i < pts.length; i++) {
        const p = { lat: pts[i].latitude, lng: pts[i].longitude };
        if (i > 0 && gapBefore[i]) {
            if (current.length > 1) segments.push(current);
            const prev = { lat: pts[i - 1].latitude, lng: pts[i - 1].longitude };
            gaps.push([prev, p]);
            current = [p];
        } else {
            current.push(p);
        }
    }
    if (current.length > 1) segments.push(current);
    return { segments, gaps };
};
